/**
 * 運営（superadmin）向け 正準スキル辞書 管理・是正画面（050）。
 *
 * スキル名の表記ゆれを吸収する「正準スキル辞書」を運営が確認・是正する。
 * - 一覧（業種で絞り込み・紐づく定義数つき）
 * - 編集（名称変更→サーバ側で再埋め込み）
 * - 統合（merge: 重複した2正準を1つに）
 * - 再マップ（remap: スキル定義を別/新規の正準へ手動割当。manual固定で自動上書きされない）
 * - 監査ログ閲覧（スキル定義IDで自動判定の根拠を確認）
 * - 運用トリガ（シード投入 / バックフィル / 再処理）
 *
 * バックエンドは require_superadmin。superadmin でない場合は 403（エラー表示）。
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Button, TextField, Table, TableHead, TableRow, TableCell,
  TableBody, Chip, CircularProgress, Alert, Stack, Dialog, DialogTitle, DialogContent,
  DialogActions, Divider, Snackbar, MenuItem,
} from '@mui/material';

import { useManagementAuth } from '../auth/ManagementAuthContext';
import { canonicalAdminApi, industryApi, getApiErrorMessage } from '../services/api';
import { CanonicalSkillAdminItem, CanonicalMappingAudit, Industry } from '../types';

const CanonicalSkillsAdmin: React.FC = () => {
  const { managementToken } = useManagementAuth();
  const token = managementToken || localStorage.getItem('management_token') || '';

  const [items, setItems] = useState<CanonicalSkillAdminItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [industryFilter, setIndustryFilter] = useState('');
  const [industries, setIndustries] = useState<Industry[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  // 編集
  const [editTarget, setEditTarget] = useState<CanonicalSkillAdminItem | null>(null);
  const [editName, setEditName] = useState('');
  const [editDesc, setEditDesc] = useState('');

  // 統合
  const [mergeSource, setMergeSource] = useState<CanonicalSkillAdminItem | null>(null);
  const [mergeTargetId, setMergeTargetId] = useState('');

  // 監査
  const [auditDefId, setAuditDefId] = useState('');
  const [auditRows, setAuditRows] = useState<CanonicalMappingAudit[] | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await canonicalAdminApi.list(token, industryFilter || undefined);
      setItems(data);
    } catch (e) {
      setError(getApiErrorMessage(e, '正準辞書の取得に失敗しました（運営権限が必要です）'));
    } finally {
      setLoading(false);
    }
  }, [token, industryFilter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    industryApi.getActive()
      .then(setIndustries)
      .catch(() => setError('業種一覧の取得に失敗しました'));
  }, []);

  const runOp = async (label: string, fn: () => Promise<unknown>) => {
    setBusy(label);
    try {
      const res = await fn();
      setToast(`${label} 完了: ${JSON.stringify(res)}`);
      await load();
    } catch (e) {
      setError(getApiErrorMessage(e, `${label} に失敗しました`));
    } finally {
      setBusy(null);
    }
  };

  const openEdit = (it: CanonicalSkillAdminItem) => {
    setEditTarget(it);
    setEditName(it.canonical_name);
    setEditDesc(it.description || '');
  };

  const saveEdit = async () => {
    if (!editTarget) return;
    await runOp('編集', () => canonicalAdminApi.update(editTarget.canonical_id, { name: editName, description: editDesc }, token));
    setEditTarget(null);
  };

  const doMerge = async () => {
    if (!mergeSource || !mergeTargetId.trim()) return;
    await runOp('統合', () => canonicalAdminApi.merge(mergeSource.canonical_id, mergeTargetId.trim(), token));
    setMergeSource(null);
    setMergeTargetId('');
  };

  const loadAudit = async () => {
    if (!auditDefId.trim()) return;
    setBusy('監査');
    try {
      const rows = await canonicalAdminApi.audit(auditDefId.trim(), token);
      setAuditRows(rows);
    } catch (e) {
      setError(getApiErrorMessage(e, '監査ログの取得に失敗しました'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, maxWidth: 1100, mx: 'auto' }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>正準スキル辞書（表記ゆれ吸収）</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        業種単位でスキル名を正規化する辞書の確認・是正を行います（運営専用）。
      </Typography>

      {/* 運用トリガ */}
      <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>運用</Typography>
        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
          <Button variant="outlined" disabled={!!busy} onClick={() => runOp('シード投入', () => canonicalAdminApi.seed(token))}>
            シード投入
          </Button>
          <Button variant="outlined" disabled={!!busy} onClick={() => runOp('バックフィル', () => canonicalAdminApi.backfill(token))}>
            バックフィル
          </Button>
          <Button variant="outlined" disabled={!!busy} onClick={() => runOp('再処理', () => canonicalAdminApi.reprocess(token, { limit: 500 }))}>
            再処理（pending/failed）
          </Button>
          {busy && <Chip size="small" label={`${busy}中…`} />}
        </Stack>
      </Paper>

      {/* 絞り込み */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }} alignItems={{ sm: 'center' }}>
        <TextField
          select size="small" label="業種で絞り込み" value={industryFilter}
          onChange={(e) => setIndustryFilter(e.target.value)} sx={{ flex: 1 }}
        >
          <MenuItem value="">すべての業種</MenuItem>
          {industries.map((ind) => (
            <MenuItem key={ind.id} value={ind.id}>{ind.name}</MenuItem>
          ))}
        </TextField>
        <Button variant="contained" onClick={load} disabled={loading}>再読み込み</Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* 一覧 */}
      <Paper sx={{ borderRadius: 2, overflowX: 'auto', mb: 3 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
        ) : items.length === 0 ? (
          <Box py={4} textAlign="center"><Typography color="text.secondary">正準スキルがありません</Typography></Box>
        ) : (
          <Table size="small" sx={{ minWidth: 480 }}>
              <TableHead>
                <TableRow sx={{ '& th': { whiteSpace: 'nowrap' } }}>
                  <TableCell>正準名</TableCell>
                  <TableCell>由来</TableCell>
                  <TableCell align="right">紐づく定義数</TableCell>
                  <TableCell align="center">埋め込み</TableCell>
                  <TableCell align="right">操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((it) => (
                  <TableRow key={it.canonical_id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{it.canonical_name}</Typography>
                      <Typography variant="caption" color="text.secondary">{it.canonical_id}</Typography>
                    </TableCell>
                    <TableCell><Chip size="small" label={it.source} /></TableCell>
                    <TableCell align="right">{it.definition_count}</TableCell>
                    <TableCell align="center">{it.has_embedding ? '✓' : '—'}</TableCell>
                    <TableCell align="right">
                      <Button size="small" onClick={() => openEdit(it)}>編集</Button>
                      <Button size="small" color="warning" onClick={() => { setMergeSource(it); setMergeTargetId(''); }}>統合元にする</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
        )}
      </Paper>

      {/* 監査ログ */}
      <Paper sx={{ p: 2, borderRadius: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>監査ログ（スキル定義の自動判定の根拠）</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 1.5 }}>
          <TextField size="small" label="skill_definition_id" value={auditDefId}
            onChange={(e) => setAuditDefId(e.target.value)} sx={{ flex: 1 }} />
          <Button variant="outlined" onClick={loadAudit} disabled={!!busy}>取得</Button>
        </Stack>
        {auditRows && (auditRows.length === 0 ? (
          <Typography variant="body2" color="text.secondary">監査ログはありません</Typography>
        ) : (
          <Stack spacing={1} divider={<Divider />}>
            {auditRows.map((r) => (
              <Box key={r.id}>
                <Typography variant="body2">
                  <b>{r.decision}</b> / method={r.method ?? '-'} / confidence={r.confidence ?? '-'} / {r.created_at ?? ''}
                </Typography>
                {r.reason && <Typography variant="caption" color="text.secondary">理由: {r.reason}</Typography>}
                {r.error && <Typography variant="caption" color="error">error: {r.error}</Typography>}
              </Box>
            ))}
          </Stack>
        ))}
      </Paper>

      {/* 編集ダイアログ */}
      <Dialog open={!!editTarget} onClose={() => setEditTarget(null)} fullWidth maxWidth="sm">
        <DialogTitle>正準スキルを編集</DialogTitle>
        <DialogContent>
          <TextField fullWidth margin="normal" label="正準名" value={editName} onChange={(e) => setEditName(e.target.value)} />
          <TextField fullWidth margin="normal" label="説明" value={editDesc} onChange={(e) => setEditDesc(e.target.value)} multiline minRows={2} />
          <Typography variant="caption" color="text.secondary">※ 名称/説明を変更すると埋め込みが再生成されます。</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditTarget(null)}>キャンセル</Button>
          <Button variant="contained" onClick={saveEdit} disabled={!!busy}>保存</Button>
        </DialogActions>
      </Dialog>

      {/* 統合ダイアログ */}
      <Dialog open={!!mergeSource} onClose={() => setMergeSource(null)} fullWidth maxWidth="sm">
        <DialogTitle>正準スキルを統合</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            統合元「{mergeSource?.canonical_name}」に紐づく定義を、統合先へ付け替えます（統合元は無効化）。
          </Typography>
          <TextField fullWidth margin="normal" label="統合先の canonical_id" value={mergeTargetId}
            onChange={(e) => setMergeTargetId(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMergeSource(null)}>キャンセル</Button>
          <Button variant="contained" color="warning" onClick={doMerge} disabled={!!busy || !mergeTargetId.trim()}>統合</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)} message={toast ?? ''} />
    </Box>
  );
};

export default CanonicalSkillsAdmin;
